import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, toArray } from 'rxjs/operators';

import { ToastService } from '../../../modules/shared/services/toast-message.service';
import { ScanConfigurationService } from '../../../services/scan-configuration/scan-configuration.service';
import {
  IAssetList,
  IExposureMonitoring,
  IScanConfig,
  IScanConfigNetworkAsset,
  IScanConfigNetworkOverview,
  IScanConfigWebAppCreate,
  IScanConfigWebAppOverview,
} from './models/scan-configuration';
import * as actions from './scan-configuration.actions';

@Injectable()
export class ScanConfigurationEffects {
  private actions$ = inject(Actions);
  private service = inject(ScanConfigurationService);
  private toastService = inject(ToastService);

  getScanConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getScanConfig),
      exhaustMap(() =>
        this.service.getScanConfig().pipe(
          map((res: IScanConfig) => {
            return actions.getScanConfigSuccess({ res });
          }),
          catchError((error: any) => of(actions.getScanConfigFailure(error))),
        ),
      ),
    ),
  );

  getWebApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getWebApp),
      exhaustMap(({ id }) =>
        this.service.getWebApp(id).pipe(
          map((res: IScanConfigWebAppOverview) => {
            return actions.getWebAppSuccess({ res });
          }),
          catchError((error: any) => of(actions.getWebAppFailure(error))),
        ),
      ),
    ),
  );

  getNetwork$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getNetwork),
      exhaustMap(({}) =>
        this.service.getNetwork().pipe(
          map((res: IScanConfigNetworkOverview[]) => {
            return actions.getNetworkSuccess({ res });
          }),
          catchError((error: any) => of(actions.getNetworkFailure(error))),
        ),
      ),
    ),
  );

  editNetworkAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editNetworkAssets),
      exhaustMap(({ hosts }) =>
        this.service.editNetworkAssets(hosts).pipe(
          map(() => actions.editNetworkAssetsSuccess()),
          catchError((error: any) =>
            of(actions.editNetworkAssetsFailure(error)),
          ),
        ),
      ),
    ),
  );

  editNetworkAssetsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editNetworkAssetsSuccess),
      map(() => {
        this.toastService.success('Network scan configuration updated.');
        return actions.getScanConfig();
      }),
    ),
  );

  createWebApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createWebApp),
      exhaustMap(({ data }) =>
        this.service.createWebApp(data).pipe(
          map((data: IScanConfigWebAppCreate) => {
            return actions.createWebAppSuccess({ data });
          }),
          catchError((error: any) => of(actions.createWebAppFailure(error))),
        ),
      ),
    ),
  );

  createWebAppSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createWebAppSuccess),
      map(() => {
        this.toastService.success('Web App is added.');
        return actions.getScanConfig();
      }),
    ),
  );

  getNetworkAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getNetworkAssets),
      exhaustMap(() =>
        this.service.getNetworkAssets().pipe(
          map((res: IScanConfigNetworkAsset[]) => {
            return actions.getNetworkAssetsSuccess({ res });
          }),
          catchError((error: any) =>
            of(actions.getNetworkAssetsFailure(error)),
          ),
        ),
      ),
    ),
  );

  createNetwork$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createNetwork),
      exhaustMap(({ assetIds }) =>
        this.service.createNetwork(assetIds).pipe(
          map(() => {
            return actions.createNetworkSuccess();
          }),
          catchError((error: any) => of(actions.createNetworkFailure(error))),
        ),
      ),
    ),
  );

  createNetworkSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createNetworkSuccess),
      map(() => {
        this.toastService.success('Network is added.');
        return actions.getScanConfig();
      }),
    ),
  );

  updateNetwork$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateNetwork),
      exhaustMap(({ id, assetIds }) =>
        this.service.updateNetwork(id, assetIds).pipe(
          map(() => {
            return actions.updateNetworkSuccess();
          }),
          catchError((error: any) => of(actions.updateNetworkFailure(error))),
        ),
      ),
    ),
  );

  updateNetworkSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateNetworkSuccess),
      map(() => {
        this.toastService.success('Network is updated.');
        return actions.getScanConfig();
      }),
    ),
  );

  removeConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeConfig),
      exhaustMap(({ id }) =>
        this.service.removeConfig(id).pipe(
          map(() => {
            return actions.removeConfigSuccess();
          }),
          catchError((error: any) => of(actions.removeConfigFailure(error))),
        ),
      ),
    ),
  );

  removeConfigSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeConfigSuccess),
      map(() => {
        this.toastService.success('Configuration is removed');
        return actions.getScanConfig();
      }),
    ),
  );

  getExposureMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getExposureMonitoring),
      exhaustMap(() =>
        this.service.getExposureMonitoring().pipe(
          map((res: IExposureMonitoring) =>
            actions.getExposureMonitoringSuccess({ res }),
          ),
          catchError((error: any) =>
            of(actions.getExposureMonitoringFailure(error)),
          ),
        ),
      ),
    ),
  );

  // Unmonitor before monitor: the API enforces the plan limit per call, so freeing the
  // headroom first lets a swap (drop one, add one) succeed at the limit.
  saveExposureMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveExposureMonitoring),
      exhaustMap(({ monitor, unmonitor }) => {
        const steps = [];
        if (unmonitor.length) {
          steps.push(this.service.setExposureMonitoring(unmonitor, false));
        }
        if (monitor.length) {
          steps.push(this.service.setExposureMonitoring(monitor, true));
        }
        return concat(...steps).pipe(
          toArray(),
          map(() => actions.saveExposureMonitoringSuccess()),
          catchError((error: any) =>
            of(actions.saveExposureMonitoringFailure(error)),
          ),
        );
      }),
    ),
  );

  saveExposureMonitoringSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveExposureMonitoringSuccess),
      switchMap(() => {
        this.toastService.success('Exposure monitoring updated.');
        return [actions.getScanConfig(), actions.getExposureMonitoring()];
      }),
    ),
  );

  // The interceptor has already shown the error; the first call may have gone through,
  // so reload to show what actually persisted.
  saveExposureMonitoringFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveExposureMonitoringFailure),
      switchMap(() => [actions.getScanConfig(), actions.getExposureMonitoring()]),
    ),
  );

  getAssetList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetList),
      exhaustMap(() =>
        this.service.getAssetList().pipe(
          map((assetList: IAssetList[]) => {
            return actions.getAssetListSuccess({ assetList });
          }),
          catchError((error: any) => of(actions.getAssetListFailure(error))),
        ),
      ),
    ),
  );
}
